import React from 'react';
import { Card, Table, Badge } from 'react-bootstrap';
import DonationGraph from './DonationGraph';
import { SimulationConfig, Donation, Donor, Project } from '../models/SimulationModels';

interface DonationsListProps {
  donations: Donation[];
  donors: Donor[];
  donorInfluence: Array<{
    donorId: string;
    contributionAmount: number;
    matchingGenerated: number;
    influencePercentage: number;
  }>;
  config: SimulationConfig;
  selectedProject: Project | null;
  allDonations: Donation[]; // All donations in the system, not just for this project
}

const DonationsList: React.FC<DonationsListProps> = ({ 
  donations, 
  donors, 
  donorInfluence, 
  config, 
  selectedProject,
  allDonations 
}) => {
  // Create enhanced donations with matching information
  const enhancedDonations = donations.map(donation => {
    const donorInfluenceData = donorInfluence.find(d => d.donorId === donation.donorId);
    const donor = donors.find(d => d.id === donation.donorId);
    
    return {
      ...donation,
      donorName: donor?.name || 'Unknown',
      matchingGenerated: donorInfluenceData?.matchingGenerated || 0,
      influencePercentage: donorInfluenceData?.influencePercentage || 0
    };
  });
  
  // Sort donations by matching generated (descending)
  const sortedDonations = [...enhancedDonations].sort((a, b) => 
    b.matchingGenerated - a.matchingGenerated
  );

  return (
    <>
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white border-0">
          <h5 className="mb-0">Recent Donations</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Donor</th>
                  <th>Amount</th>
                  <th>Matching</th>
                  <th>Impact</th>
                </tr>
              </thead>
              <tbody>
                {sortedDonations.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      No donations yet. Be the first to donate!
                    </td>
                  </tr>
                ) : (
                  sortedDonations.map(donation => (
                    <tr key={donation.id}>
                      <td>{donation.donorName}</td>
                      <td>€{donation.amount.toFixed(2)}</td>
                      <td>€{donation.matchingGenerated.toFixed(2)}</td>
                      <td>
                        <Badge 
                          bg={donation.influencePercentage > 5 ? 'success' : 'secondary'}
                        >
                          {donation.influencePercentage.toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      
      <DonationGraph 
        donations={enhancedDonations.map(donation => ({
          amount: donation.amount,
          matchingGenerated: donation.matchingGenerated,
        }))}
        config={config}
        selectedProject={selectedProject}
        allDonations={allDonations}
        allDonors={donors}
      />
    </>
  );
};

export default DonationsList; 